import type { JsonValue } from "@e308/core";
import { INFRASTRUCTURE_TARGETS } from "./combat-types.js";
import type { MatchLogPayload } from "./diplomacy-types.js";
import { microFromString } from "./exact-numbers.js";
import { exactRecordKeys as exactKeys, recordObject } from "./record-validation.js";

const KINDS = [
  "pact-proposed",
  "pact-accepted",
  "pact-notice-issued",
  "pact-ended",
  "metal-shipped",
  "metal-delivered",
  "metal-lost",
  "damage-summary",
  "production-milestone",
  "controller-changed",
  "room-pause-changed",
  "incident-warning",
  "incident-resolved",
  "incident-cancelled",
] as const;

export function validateMatchLog(value: unknown): MatchLogPayload & JsonValue {
  const source = recordObject(value, "match log event");
  if (!KINDS.includes(source.kind as (typeof KINDS)[number]))
    throw new TypeError("Invalid match log kind");
  const kind = source.kind as (typeof KINDS)[number];
  if (kind.startsWith("pact-")) validatePactLog(source, kind);
  else if (kind.startsWith("metal-")) validateShipmentLog(source);
  else if (kind === "damage-summary") validateDamageLog(source);
  else if (kind === "production-milestone") {
    exactKeys(source, ["kind", "seatId", "itemId"]);
    strings(source, ["seatId", "itemId"]);
  } else if (kind === "controller-changed") validateControllerLog(source);
  else if (kind === "room-pause-changed") {
    exactKeys(source, ["kind", "paused"]);
    if (typeof source.paused !== "boolean") throw new TypeError("Invalid room pause state");
  } else validateIncidentLog(source);
  return Object.freeze({ ...source }) as MatchLogPayload & JsonValue;
}

function validateControllerLog(source: Record<string, unknown>): void {
  exactKeys(source, ["kind", "seatId", "sessionId", "generation", "mode"]);
  strings(source, ["seatId", "sessionId"]);
  if (!Number.isSafeInteger(source.generation) || (source.generation as number) < 1)
    throw new TypeError("Invalid controller generation");
  if (source.mode !== "human" && source.mode !== "autopilot")
    throw new TypeError("Invalid controller mode");
}

function validatePactLog(source: Record<string, unknown>, kind: string): void {
  const fields = ["kind", "actorId", "targetId"];
  if (kind === "pact-proposed") fields.push("message");
  if (kind === "pact-notice-issued") fields.push("expiresAtMs");
  exactKeys(source, fields);
  strings(source, ["actorId", "targetId"]);
  if (kind === "pact-proposed" && source.message !== null && typeof source.message !== "string")
    throw new TypeError("Invalid pact message");
  if (kind === "pact-notice-issued") safeTime(source.expiresAtMs);
}

function validateShipmentLog(source: Record<string, unknown>): void {
  exactKeys(source, ["kind", "shipmentId", "senderId", "recipientId", "amount"]);
  strings(source, ["shipmentId", "senderId", "recipientId", "amount"]);
  if (microFromString(source.amount as string) <= 0n)
    throw new TypeError("Invalid shipment amount");
}

function validateDamageLog(source: Record<string, unknown>): void {
  exactKeys(source, ["kind", "windowEndsAtMs", "entries"]);
  safeTime(source.windowEndsAtMs);
  if (!Array.isArray(source.entries)) throw new TypeError("Invalid damage entries");
  for (const value of source.entries) {
    const entry = recordObject(value, "damage entry");
    exactKeys(entry, [
      "attackerId",
      "targetId",
      "effectiveTarget",
      "shieldAbsorbed",
      "troopDamage",
      "infrastructureDamage",
      "blastDoorsActive",
    ]);
    strings(entry, [
      "attackerId",
      "targetId",
      "effectiveTarget",
      "shieldAbsorbed",
      "troopDamage",
      "infrastructureDamage",
    ]);
    if (!INFRASTRUCTURE_TARGETS.includes(entry.effectiveTarget as never))
      throw new TypeError("Invalid damage target");
    for (const key of ["shieldAbsorbed", "troopDamage", "infrastructureDamage"])
      if (microFromString(entry[key] as string) < 0n) throw new TypeError("Invalid damage amount");
    if (typeof entry.blastDoorsActive !== "boolean") throw new TypeError("Invalid damage report");
  }
}

function validateIncidentLog(source: Record<string, unknown>): void {
  exactKeys(source, ["kind", "incident"]);
  const incident = recordObject(source.incident, "incident");
  exactKeys(incident, ["id", "targetId", "effect", "announcedAtMs", "resolvesAtMs"]);
  if (!["meteor-warning", "valley-salvage-train", "clear-skies"].includes(incident.id as string))
    throw new TypeError("Invalid incident");
  if (incident.targetId !== null && typeof incident.targetId !== "string")
    throw new TypeError("Invalid incident target");
  if (
    !["200-power-generator-damage", "100-metal-each", "50-shield-charge-each"].includes(
      incident.effect as string,
    )
  )
    throw new TypeError("Invalid incident effect");
  safeTime(incident.announcedAtMs);
  safeTime(incident.resolvesAtMs);
}

function strings(source: Record<string, unknown>, keys: readonly string[]): void {
  if (keys.some((key) => typeof source[key] !== "string")) throw new TypeError("Invalid log text");
}

function safeTime(value: unknown): void {
  if (!Number.isSafeInteger(value) || (value as number) < 0)
    throw new TypeError("Invalid log time");
}
